<script setup>
/**
 * ImmersivePriceArea
 * "Smart price + badges" display for the immersive catalog. Pure presentational:
 * all values come from useItemPricing (computed by the parent overlay). Shows the
 * effective price, struck-through original, a promo/discount badge and the line
 * total incl. VAT. Every element here is either static per item, height-reserved,
 * or lives inside the nowrap price row — the block NEVER changes height when the
 * cart changes (the "buy N more" next-step hint is a toast in the host overlay).
 * The nowrap row scales itself down (transform, not font-size — width-exact and
 * layout-height-neutral) when it outgrows the column; see refitPriceRow.
 */
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  showPrices: { type: Boolean, default: true },
  hasValidPrice: { type: Boolean, default: false },
  hasPromo: { type: Boolean, default: false },
  // Item belongs to an eligible promo (madregot) even before a step applies —
  // shows the "מבצע" badge up front, like the promotions-department cards.
  hasEligiblePromo: { type: Boolean, default: false },
  // Preformatted "₪base - ₪best" promo range line (null when no promo).
  promoPriceRange: { type: String, default: null },
  promoIsFirstOrder: { type: Boolean, default: false },
  hasDiscount: { type: Boolean, default: false },
  discountPercent: { type: Number, default: 0 },
  formattedPrice: { type: String, default: '—' },
  originalPrice: { type: String, default: null },
  lineTotalWithVat: { type: String, default: null },
  isInCart: { type: Boolean, default: false },
})

// ── Fit-to-width ─────────────────────────────────────────────────
// The nowrap price row (struck original + price + badge) can outgrow the
// content column — it's narrowed by the ladder gutters on mobile, and the
// price gains digits as qty rises. Scale the WHOLE row to the exact available
// width instead of stepping font sizes: transform keeps the layout height
// untouched (pane stability rule) and offsetWidth/scrollWidth ignore the
// transform, so the measurement never feeds back into itself.
const rootRef = ref(null)
const mainRef = ref(null)
const fitScale = ref(1)

function refitPriceRow() {
  const row = mainRef.value
  const avail = rootRef.value?.clientWidth || 0
  const needed = row ? row.scrollWidth : 0
  // 0.55 floor keeps pathological content readable over a tiny clip.
  fitScale.value = avail > 0 && needed > avail ? Math.max(0.55, avail / needed) : 1
}

const fitStyle = computed(() =>
  fitScale.value < 1 ? { transform: `scale(${fitScale.value})` } : null
)

// Re-measure whenever anything that renders inside the row changes.
watch(
  () => [
    props.formattedPrice,
    props.originalPrice,
    props.hasPromo,
    props.hasDiscount,
    props.hasEligiblePromo,
    props.promoIsFirstOrder,
  ],
  () => nextTick(refitPriceRow)
)

// The row itself mounts/unmounts with the guest / no-price branches.
watch(mainRef, () => nextTick(refitPriceRow))

// Column width changes (resize, orientation, breakpoint reflow) — observe the
// always-present root; the observer also fires once on observe (initial fit).
let fitObserver = null
onMounted(() => {
  if (typeof window.ResizeObserver === 'function' && rootRef.value) {
    fitObserver = new window.ResizeObserver(refitPriceRow)
    fitObserver.observe(rootRef.value)
  } else {
    nextTick(refitPriceRow)
  }
})

onBeforeUnmount(() => {
  if (fitObserver) fitObserver.disconnect()
})
</script>

<template>
  <div ref="rootRef" class="imm-price">
    <!-- Guest: no prices -->
    <div v-if="!showPrices" class="imm-price-guest">התחבר לצפייה במחירים</div>

    <!-- No price available -->
    <div v-else-if="!hasValidPrice" class="imm-price-none">אין מחיר זמין</div>

    <template v-else>
      <div ref="mainRef" class="imm-price-main" :style="fitStyle">
        <span v-if="hasPromo || hasDiscount" class="imm-price-original">₪{{ originalPrice }}</span>
        <span
          class="imm-price-value"
          :class="{ 'is-promo': hasPromo, 'is-gold': hasPromo && promoIsFirstOrder }"
          ><span class="imm-price-cur">₪</span>{{ formattedPrice }}</span
        >

        <!-- Promo badge — shown whenever the item carries an eligible promo,
             so the promo identity reads before the first unit is added too. -->
        <span
          v-if="hasPromo || hasEligiblePromo"
          class="imm-price-badge"
          :class="promoIsFirstOrder ? 'badge-gold' : 'badge-red'"
        >
          {{ promoIsFirstOrder ? 'מבצע לקוחות חדשים' : 'מבצע' }}
        </span>
        <!-- Amount discount badge -->
        <span v-else-if="hasDiscount" class="imm-price-badge badge-green"
          >-{{ discountPercent }}%</span
        >
      </div>

      <!-- Promo price range: full price down to the best step price -->
      <div
        v-if="promoPriceRange"
        class="imm-price-range"
        :class="{ 'is-gold': promoIsFirstOrder }"
        dir="ltr"
      >
        {{ promoPriceRange }}
      </div>

      <!-- Line total (incl. VAT). Always occupies its line (reserved) so the
           controls below never shift down when it appears on first add. -->
      <div class="imm-price-total" :class="{ 'is-reserved': !(isInCart && lineTotalWithVat) }">
        <template v-if="isInCart && lineTotalWithVat"
          >סה"כ ₪{{ lineTotalWithVat }} כולל מע"מ</template
        >
      </div>
    </template>
  </div>
</template>

<style scoped>
.imm-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  width: 100%;
}

.imm-price-main {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Single line: the struck original + discount badge sit beside the price rather
     than wrapping to a new line (which would push the controls below down). */
  flex-wrap: nowrap;
  gap: 8px;
  /* Fit-to-width scale (see script). The row is CENTERED in the column on
     mobile/tablet, so shrinking around its own center lands exactly inside. */
  transform-origin: center;
}

/* PC start-aligns the row (physical RIGHT in RTL) — anchor the scale there. */
@media (min-width: 1280px) {
  .imm-price-main {
    transform-origin: 100% 50%;
  }
}

.imm-price-original {
  font-size: var(--imm-orig-size, 0.95rem);
  font-weight: 600;
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.5));
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
}

.imm-price-value {
  font-size: var(--imm-price-size, 2rem);
  font-weight: 800;
  line-height: 1;
  color: var(--imm-text, #ffffff);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  transition: color 0.25s ease;
}

/* The promo price is LIVE (a madregot step applies): read it in the promo's
   red — gold for first-order — matching the promotions-department identity. */
.imm-price-value.is-promo {
  color: #ef4444;
  text-shadow: 0 0 18px rgba(239, 68, 68, 0.35);
}

.imm-price-value.is-promo.is-gold {
  color: #f59e0b;
  text-shadow: 0 0 18px rgba(245, 158, 11, 0.35);
}

.imm-price-cur {
  font-size: 0.6em;
  margin-inline-end: 2px;
  opacity: 0.85;
}

/* Promo range line — small, promo-red (gold for first-order), LTR so the
   base→best order reads consistently inside the RTL layout. */
.imm-price-range {
  font-size: 0.76rem;
  font-weight: 700;
  color: #f87171;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}

.imm-price-range.is-gold {
  color: #fbbf24;
}

.imm-price-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.badge-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.badge-gold {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

/* Mobile: the promo badge moves to the pane's corner stack (above the makat
   sticker — see ImmersivePane) so the nowrap row keeps only price + original.
   The green amount-discount badge is short and stays in the row. */
@media (max-width: 767px) {
  .imm-price-badge.badge-red,
  .imm-price-badge.badge-gold {
    display: none;
  }
}

.badge-green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.imm-price-total {
  font-size: var(--imm-total-size, 0.85rem);
  font-weight: 700;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.82));
  /* Soft accent glow while the line total is live (dark theme only — the light
     theme overrides --imm-total-glow to none). */
  text-shadow: var(--imm-total-glow, 0 0 14px rgba(96, 165, 250, 0.4));
  /* Reserve exactly one line (fixed line-height, never wraps) even when empty, so
     first-add fills the line in place instead of growing the price block. */
  line-height: 1.3;
  min-height: 1.3em;
  white-space: nowrap;
}

.imm-price-total.is-reserved {
  visibility: hidden;
}

.imm-price-guest,
.imm-price-none {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.65));
}
</style>
